import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";
import { formatDateTime } from "../../lib/format";

export function AdminCouponsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminCoupons,
    queryFn: () => adminApiClient.listCoupons({ limit: 100 }),
  });

  const [code, setCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState("10");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const createCoupon = useMutation({
    mutationFn: () =>
      adminApiClient.createCoupon({
        code,
        discountBps: Math.round(Number(discountPercent) * 100),
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      setCode("");
      setDiscountPercent("10");
      setMaxRedemptions("");
      setExpiresAt("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCoupons });
    },
  });

  const deactivateCoupon = useMutation({
    mutationFn: (couponId: string) => adminApiClient.deactivateCoupon(couponId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.adminCoupons }),
  });

  return (
    <div className="stack">
      <div>
        <h1>Coupons</h1>
        <p className="muted text-sm">
          First-purchase discount codes. The discount comes out of the seller&apos;s proceeds on
          that one sale (the platform fee itself can&apos;t be discounted per-order), so every
          coupon is capped at 20% off and only redeemable by a buyer who has never completed a
          purchase before.
        </p>
      </div>

      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          createCoupon.mutate();
        }}
      >
        <div className="row" style={{ flexWrap: "wrap", gap: "1rem" }}>
          <div className="field" style={{ minWidth: 180 }}>
            <label htmlFor="coupon-code">Code</label>
            <input
              id="coupon-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="WELCOME10"
              maxLength={40}
              required
            />
          </div>
          <div className="field" style={{ minWidth: 140 }}>
            <label htmlFor="coupon-discount">Discount %</label>
            <input
              id="coupon-discount"
              type="number"
              min={1}
              max={20}
              step={1}
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              required
            />
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <label htmlFor="coupon-max-redemptions">Max redemptions (optional)</label>
            <input
              id="coupon-max-redemptions"
              type="number"
              min={1}
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="Unlimited"
            />
          </div>
          <div className="field" style={{ minWidth: 180 }}>
            <label htmlFor="coupon-expires">Expires (optional)</label>
            <input
              id="coupon-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>

        {createCoupon.isError && (
          <p className="field-error-text" role="alert">
            {describeError(createCoupon.error)}
          </p>
        )}

        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={createCoupon.isPending}>
            Create coupon
          </button>
        </div>
      </form>

      {isLoading && <LoadingState label="Loading coupons…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}

      {data && (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Redemptions</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.code}</td>
                  <td>{(c.discountBps / 100).toFixed(0)}%</td>
                  <td>
                    {c.redemptionCount}
                    {c.maxRedemptions !== null ? ` / ${c.maxRedemptions}` : ""}
                  </td>
                  <td>{c.expiresAt ? formatDateTime(c.expiresAt) : "—"}</td>
                  <td>
                    <span className={c.isActive ? "badge badge--success" : "badge badge--danger"}>
                      {c.isActive ? "Active" : "Deactivated"}
                    </span>
                  </td>
                  <td>{formatDateTime(c.createdAt)}</td>
                  <td>
                    {c.isActive && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={deactivateCoupon.isPending}
                        onClick={() => deactivateCoupon.mutate(c.id)}
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length === 0 && <p className="muted">No coupons yet.</p>}
        </div>
      )}
    </div>
  );
}
